import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Map, Store, Gem, ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EPLogo, HarrodsLogo } from "@/components/Brand";

const ICONS = { head_office: Building2, regional_manager: Map, store_manager: Store, concession_manager: Gem };
const ACCENT = { head_office: "#FF0000", regional_manager: "#00573F", store_manager: "#C5A059", concession_manager: "#0F172A" };

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/auth/demo-users").then((r) => setUsers(r.data)); }, []);
  if (user) return <Navigate to="/dashboard" replace />;

  const pick = async (u) => {
    setBusy(u.id);
    try { await login(u.id); toast.success(`Signed in as ${u.role_label}`); nav("/dashboard"); }
    catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="grain relative hidden overflow-hidden bg-[#00573F] lg:block">
        <img src="https://images.unsplash.com/photo-1649660164469-b1fa86701a7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Harrods at night" className="absolute inset-0 h-full w-full object-cover opacity-45 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#003D2C] via-[#003D2C]/50 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <HarrodsLogo variant="gold" className="h-16" />
          <div className="max-w-md text-white">
            <div className="label-xs !text-[#E6C687]">Concession & store supplies</div>
            <p className="mt-3 font-display text-4xl font-semibold leading-tight">Every carrier bag, till roll and ribbon — ordered, approved and delivered.</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 py-12 sm:px-14">
        <div className="rise mb-10"><EPLogo variant="dark" className="h-12 sm:h-14" /></div>
        <div className="rise label-xs !text-[#FF0000]" style={{ animationDelay: "60ms" }}>EP Group Harrods Management</div>
        <h1 className="rise mt-2 text-4xl font-bold tracking-tight sm:text-5xl" style={{ animationDelay: "100ms" }}>Choose a demo role</h1>
        <p className="rise mt-3 max-w-md text-sm text-slate-600" style={{ animationDelay: "140ms" }}>Each account is linked to a role and sees only the regions, stores or concessions it is responsible for.</p>
        <div className="mt-8 grid gap-3">
          {users.map((u, i) => {
            const Icon = ICONS[u.role];
            return (
              <button key={u.id} onClick={() => pick(u)} disabled={!!busy} data-testid={`role-select-${u.role.replace(/_/g, "-")}`}
                className="rise group flex items-center gap-4 rounded-lg border bg-white p-4 text-left transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-slate-900 hover:shadow-lg disabled:opacity-60"
                style={{ animationDelay: `${200 + i * 70}ms` }}>
                <span className="flex h-11 w-11 items-center justify-center rounded-md text-white" style={{ background: ACCENT[u.role] }}><Icon className="h-5 w-5" /></span>
                <span className="flex-1">
                  <span className="block font-display text-lg font-semibold">{u.role_label}</span>
                  <span className="block text-xs text-slate-500">{u.name} · {u.scope_label}</span>
                </span>
                <ArrowRight className="h-5 w-5 text-slate-300 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-[#FF0000]" />
              </button>
            );
          })}
        </div>
        <div className="mt-10 flex items-center gap-3 text-xs text-slate-400 lg:hidden">Built for <HarrodsLogo className="h-5" /></div>
      </div>
    </div>
  );
}
